import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { DataBoxTimeDividedMessageList } from '@gov-nx/module/data-box';
import {
	DATABOX_TIME_DIVISION_LABELS,
	DataBoxFolderType,
	isDataBoxTypePO,
	useDataBoxMessageListContext,
} from '@gov-nx/module/data-box';
import { PageCode } from '@gov-nx/module/page';
import { EmptyMessage } from '@gov-nx/ui/web';
import { pageRouteResolver } from '@gov-nx/web';
import { DataBoxMessageItem } from './DataBoxMessageItem';

interface DataBoxMessageListProps {
	messageList?: DataBoxTimeDividedMessageList;
}

export const DataBoxMessageList = ({
	messageList,
}: DataBoxMessageListProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tn = getLocalizeCurried(PageCode['datove-schranky']);
	const { t } = useTranslation([PageCode['datove-schranky']]);

	const { extraConfig } = useExtraConfigContext();

	const {
		dataBox,
		messagesFilter: { folderType },
		controls,
	} = useDataBoxMessageListContext();

	if (!messageList?.length && !controls.processLoading) {
		if (folderType === DataBoxFolderType.Archived) {
			return (
				<div className="flex h-full justify-center items-center">
					<EmptyMessage
						headline={null}
						image="empty">
						<p className="text-headline-xs! text-(--text-primary)!">
							{tn('zpravy.vypis.prazdna-slozka')}
						</p>
						{!isDataBoxTypePO(dataBox) && dataBox?.odpojeno === false ? (
							<p>
								<Trans
									t={t}
									i18nKey="zpravy.vypis.vytvoreni-kopie"
									components={{
										odkaz: (
											<Link to={pageRouteResolver(PageCode['nastaveni'])} />
										),
									}}
								/>
							</p>
						) : null}
					</EmptyMessage>
				</div>
			);
		}

		if (folderType === DataBoxFolderType.Trash) {
			return (
				<div className="flex h-full justify-center items-center text-center">
					<EmptyMessage
						headline={null}
						image="empty">
						<p className="text-headline-xs! text-(--text-primary)!">
							{tn('zpravy.vypis.prazdna-slozka')}
						</p>
						{extraConfig.pageRoutes.databoxConfig
							.displayArchiveInEmptyFolderState &&
							!isDataBoxTypePO(dataBox) && (
								<p>
									<Trans
										t={t}
										i18nKey="zpravy.vypis.kos-kopie"
										components={{ 'novy-radek': <br /> }}
									/>
								</p>
							)}
						<p>
							<strong>
								<Trans
									t={t}
									i18nKey="zpravy.vypis.nemazat-ze-slozky"
									components={{ 'novy-radek': <br /> }}
								/>
							</strong>
							<br />
							{tn('zpravy.vypis.smazou-samy')}
						</p>
					</EmptyMessage>
				</div>
			);
		}

		return (
			<div className="flex h-full justify-center items-center">
				<EmptyMessage
					headline={null}
					image="empty">
					<p className="text-headline-xs! text-(--text-primary)!">
						{tn('zpravy.vypis.prazdna-slozka')}
					</p>
				</EmptyMessage>
			</div>
		);
	}

	return (
		<ul className="gov-list--plain mt-0!">
			{messageList?.map((timePeriod) => (
				<li key={timePeriod.label}>
					<h2 className="px-(--spacing-m) py-(--spacing-s) text-body-s-bold! text-(--text-secondary) bg-(--background-primary-subtlest)">
						{tn(DATABOX_TIME_DIVISION_LABELS[timePeriod.label])}
					</h2>
					<ul className="gov-list--plain mt-0!">
						{timePeriod?.messages.map((message) => (
							<DataBoxMessageItem
								key={message.datovaZpravaId}
								message={message}
								timePeriodLabel={timePeriod.label}
							/>
						))}
					</ul>
				</li>
			))}
		</ul>
	);
};
